import { Grid, TextField, TextFieldProps } from '@mui/material';
import type { GridSize } from '@mui/material';
import { useEffect, useState } from 'react';
import FieldHelp from '../../components/FieldHelp';

type ValidationRule = {
    regex: RegExp;
    error: string;
};

type SttFormTextProps = {
    grid?: boolean;
    caption?: string;
    /** Texto de ayuda: si se pasa, se muestra un icono de ayuda junto al campo. */
    help?: string;
    password?: boolean;
    onChange?: ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) => void;
    defVal?: string | null;
    name?: string;
    onEnterKey?: () => void;
    validation?: ValidationRule[];
    setValidationError?: (value: boolean) => void;
    xs?: GridSize;
    sm?: GridSize;
    md?: GridSize;
    lg?: GridSize;
    xl?: GridSize;
} & Omit<TextFieldProps, 'onChange'>;

export function SttFormText({
    grid = false,
    xs = 12,
    sm = 6,
    md = 3,
    lg,
    xl,
    caption,
    help,
    password,
    onChange,
    defVal,
    name,
    onEnterKey,
    validation,
    setValidationError,
    ...rest
}: SttFormTextProps) {
    const [error, setError] = useState(false);
    const [messageError, setMessageError] = useState('');

    useEffect(() => {
        if (validation) {
            /**
             * Array con las reglas comprobadas con booleanos. El valor "TRUE" significa que la regla se cumple
             * (es decir encuentra valores que hacen match con la REGEX) y "FALSE" que no.
             */
            const arrayWithValidateData =
                validation.map(
                    (rule: any) => (defVal || '').match(rule.regex) !== null,
                ) ?? [];
            // Find first failing rule (first false value)
            const firstFailingIndex = arrayWithValidateData.findIndex(
                (element: any) => element === false,
            );
            const hasError = firstFailingIndex !== -1;
            setValidationError?.(hasError);
            setError(hasError);
            setMessageError(
                hasError ? validation[firstFailingIndex]!.error : '',
            );
        }
    }, [defVal, setValidationError, validation]);

    const field = (
        <TextField
            fullWidth
            type={password ? 'password' : 'text'}
            variant="outlined"
            label={caption}
            size="small"
            value={defVal || ''}
            name={name}
            onChange={(e: any) => onChange?.({ name, value: e.target.value })}
            onKeyUp={(event: any) => {
                if (event.key === 'Enter' && typeof onEnterKey === 'function') {
                    onEnterKey();
                }
            }}
            helperText={messageError}
            error={error}
            {...rest}
        />
    );

    const content = help ? (
        <div className="flex items-center gap-1">
            <div className="min-w-0 flex-1">{field}</div>
            <FieldHelp text={help} />
        </div>
    ) : (
        field
    );

    return grid ? (
        <Grid size={{ xs, sm, md, lg, xl }}>{content}</Grid>
    ) : (
        content
    );
}
